// STATUS — the project's health, as proven by its requirement-linked tests.
//
// Every requirement (Docs → Requirements) can carry tests; this page is where
// all of them meet: the latest run (where it ran, when, how many passed), every
// test with its own newest verdict grouped under the requirement it proves,
// the run history, and Run all. The data is /api/tests/status — served by the
// cortex backend here and by the bundled test engine (through portal-api) in a
// deployed archive, so the SAME page reads a deployment's own health.
//
// A verdict is one of passed / failed / error / skipped; a test never run says
// so. Skipped is honest, not bad: a test whose target does not exist in this
// environment (the app's public page, seen from cortex) is reported skipped —
// it will run where the target exists.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, Loader2, Play, RefreshCw, ChevronDown, ChevronRight, FlaskConical, ListChecks, Radar, Bell } from "lucide-react";
import { fetchData } from "../fetchapi/fetchapi";
import { createPageUrl, currentProjectSlug } from "@/utils";
import { isPortal } from "../utils/portal";
import { StatusDot, ResultDetails, testStatusMeta, summarize, when } from "../components/docs/FeatureTests";

// "*/15 * * * *" → "every 15 min"; anything else is shown as the cron it is.
function cronHuman(cron) {
  const m = /^\*\/(\d+) \* \* \* \*$/.exec(String(cron || "").trim());
  if (m) return `every ${m[1]} min`;
  const h = /^0 \*\/(\d+) \* \* \*$/.exec(String(cron || "").trim());
  if (h) return `every ${h[1]} h`;
  if (String(cron || "").trim() === "0 * * * *") return "every hour";
  return `on \`${cron}\``;
}

const RUN_TONE = {
  passed: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  failed: "bg-red-500/10 text-red-700 dark:text-red-400",
  error: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
  running: "bg-nav-active/10 text-nav-active",
};

export default function Status() {
  const projectSlug = currentProjectSlug();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [openRun, setOpenRun] = useState(null);   // run id whose results are shown
  const [runDetail, setRunDetail] = useState(null);
  const [filter, setFilter] = useState("all");
  const [poll, setPoll] = useState(0);

  const load = useCallback(async () => {
    setErr("");
    try { const r = await fetchData("tests/status", { method: "GET" }); setData(r); return r; }
    catch (e) { setErr(String(e?.message || e?.error || "Could not load the status.")); return null; }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Poll while a run is in flight (after Run all, or if one was already live).
  useEffect(() => {
    if (!poll && !data?.running) return undefined;
    let ticks = 0;
    const t = setInterval(async () => {
      ticks++;
      const r = await load();
      if (!r?.running || ticks > 150) { setPoll(0); if (!r?.running) clearInterval(t); }
    }, 4000);
    return () => clearInterval(t);
  }, [poll, data?.running, load]);

  const runAll = async () => {
    setBusy("run"); setMsg("");
    try {
      const r = await fetchData("tests/run", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) });
      setMsg(r?.already_running ? "A run is already in progress." : r?.empty ? "There are no tests to run yet." : `Run #${r?.run_id} started.`);
      await load(); setPoll((n) => n + 1);
    } catch (e) { setMsg(String(e?.message || "could not start the run")); }
    finally { setBusy(""); }
  };

  const showRun = async (id) => {
    if (openRun === id) { setOpenRun(null); setRunDetail(null); return; }
    setOpenRun(id); setRunDetail(null);
    try { setRunDetail(await fetchData(`tests/runs/${id}`, { method: "GET" })); }
    catch (e) { setRunDetail({ error: String(e?.message || "could not load the run") }); }
  };

  const tests = data?.tests || [];
  const features = data?.features || [];
  const counts = useMemo(() => summarize(tests), [tests]);
  const groups = useMemo(() => {
    const byKey = new Map(features.map((f) => [f.key, f]));
    const g = new Map();
    for (const t of tests) {
      if (filter !== "all" && (t.latest?.status || "none") !== filter) continue;
      const k = t.feature_key || "";
      if (!g.has(k)) g.set(k, { key: k, feature: byKey.get(k) || null, tests: [] });
      g.get(k).tests.push(t);
    }
    // Failing requirements first, then by name.
    const rank = (grp) => { const c = summarize(grp.tests); return c.error || c.failed ? 0 : c.none === c.total ? 2 : 1; };
    return [...g.values()].sort((a, b) => rank(a) - rank(b) || String(a.feature?.name || a.key).localeCompare(String(b.feature?.name || b.key)));
  }, [tests, features, filter]);

  const latest = data?.latest_run;
  const running = data?.running;
  const health = !tests.length ? "none" : counts.error || counts.failed ? "failed" : counts.none === counts.total ? "none" : counts.passed && !counts.none ? "passed" : "partial";
  const HEALTH = {
    passed: { label: "All tests passing", tone: "text-emerald-700 dark:text-emerald-400" },
    failed: { label: `${counts.failed + counts.error} test${counts.failed + counts.error === 1 ? "" : "s"} failing`, tone: "text-red-700 dark:text-red-400" },
    partial: { label: "Some tests have not run yet", tone: "text-amber-700 dark:text-amber-400" },
    none: { label: tests.length ? "Not run yet" : "No tests yet", tone: "text-page-muted" },
  }[health];

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4" data-testid="status-page">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-medium text-page-title inline-flex items-center gap-2">
            <Activity size={20} /> Status
          </h1>
          <div className="text-sm text-page-muted mt-1">
            the health of <b>{projectSlug}</b>, as proven by its requirement-linked tests — each
            requirement's tests with their latest verdict, wherever they last ran
            ({data?.env || "…"}). The same tests ship inside every deployable archive of this project.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => load()} title="Refresh" data-testid="status-refresh"
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-card-border/30">
            <RefreshCw size={13} /> Refresh
          </button>
          <button type="button" onClick={runAll} disabled={busy === "run" || !!running || !tests.length} data-testid="status-run-all"
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-emerald-500/10 disabled:opacity-50">
            {busy === "run" || running ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
            {running ? "Running…" : "Run all tests"}
          </button>
        </div>
      </div>

      {err ? <div className="text-sm text-red-600" data-testid="status-error">{err}</div> : null}
      {msg ? <div className="text-xs text-page-muted" data-testid="status-msg">{msg}</div> : null}

      {data ? (
        <div className="rounded-lg border border-page-divider p-4 flex flex-wrap items-center gap-4" data-testid="status-summary">
          <div>
            <div className={`text-lg font-medium ${HEALTH.tone}`} data-testid="status-health">{HEALTH.label}</div>
            <div className="text-xs text-page-muted mt-0.5">
              {latest
                ? <>latest run #{latest.id} · {latest.env} · {latest.trigger} · {when(latest.finished_at || latest.started_at)}{latest.started_by ? ` · by ${latest.started_by}` : ""}</>
                : "no run recorded yet"}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs ml-auto">
            {[["passed", counts.passed], ["failed", counts.failed], ["error", counts.error], ["skipped", counts.skipped], ["none", counts.none]].map(([k, n]) => (
              <button type="button" key={k} onClick={() => setFilter(filter === k ? "all" : k)} data-testid={`status-filter-${k}`}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border cursor-pointer bg-transparent ${filter === k ? "border-nav-active" : "border-page-divider"} ${testStatusMeta(k).text}`}>
                <StatusDot status={k} /> {n} {testStatusMeta(k).label}
              </button>
            ))}
            <span className="text-page-muted">of {counts.total}</span>
          </div>
        </div>
      ) : !err ? (
        <div className="flex items-center gap-2 text-sm text-page-muted"><Loader2 size={16} className="animate-spin" /> Loading…</div>
      ) : null}

      {data?.watch ? (
        // WHO WATCHES when nobody is looking (2026-09-07): the backend runs the
        // suite on a schedule, the fleet runs the browser tests, and a health
        // flip files an Alert and posts to Slack — so a broken app is known
        // before a person opens it. Shown so the promise is visible.
        <div className="rounded-lg border border-page-divider px-4 py-2.5 text-xs text-page-text flex flex-wrap items-center gap-x-5 gap-y-1" data-testid="status-watch">
          <span className="inline-flex items-center gap-1.5">
            <Radar size={13} className="text-page-muted" />
            <b>Watched:</b>
            {data.watch.schedule ? <span data-testid="status-watch-schedule">runs {cronHuman(data.watch.schedule)}</span> : <span className="text-amber-700 dark:text-amber-400">no schedule</span>}
            <span className="text-page-muted">· browser tests on the agent fleet{data.watch.browser_job ? ` (last booked ${when(data.watch.browser_job.created_at)}, ${data.watch.browser_job.state})` : ""}</span>
          </span>
          <span className="inline-flex items-center gap-1.5" data-testid="status-watch-alerts">
            <Bell size={13} className="text-page-muted" />
            <b>On a change:</b> Alerts{data.watch.slack ? <> + Slack <span className="font-mono">{data.watch.slack_channel}</span></> : <span className="text-page-muted"> (Slack not configured here)</span>}
          </span>
          {data.health ? (
            <span className={`ml-auto ${data.health.status === "red" ? "text-red-700 dark:text-red-400" : data.health.status === "green" ? "text-emerald-700 dark:text-emerald-400" : "text-page-muted"}`} data-testid="status-health-since">
              {data.health.status === "red" ? `RED since ${when(data.health.changed_at)}` : data.health.status === "green" ? `green since ${when(data.health.changed_at)}` : "no verdict yet"}
              <span className="text-page-muted"> · checked {when(data.health.checked_at)}</span>
            </span>
          ) : <span className="ml-auto text-page-muted" data-testid="status-health-since">no health verdict recorded yet</span>}
        </div>
      ) : null}

      {data && !tests.length ? (
        <div className="rounded-lg border border-page-divider p-4 text-sm text-page-text space-y-2" data-testid="status-empty">
          <div className="font-medium inline-flex items-center gap-2"><FlaskConical size={14} /> No tests yet.</div>
          <div className="text-page-muted">
            Tests belong to requirements. {isPortal() ? "This deployment shipped without tests." : (
              <>Open <Link to={createPageUrl("docs?tab=features")} className="text-nav-active">Docs → Requirements</Link>, expand a requirement and
              press <b>Write tests</b> (the agent fleet derives them from its use cases) or <b>Add test</b>.
              Every test written there runs here and inside every deployable archive of the project.</>
            )}
          </div>
        </div>
      ) : null}

      <div className="space-y-2">
        {groups.map((grp) => {
          const c = summarize(grp.tests);
          const worst = c.error ? "error" : c.failed ? "failed" : c.none === c.total ? "none" : c.passed ? "passed" : "skipped";
          return (
            <div key={grp.key || "_"} className="rounded-lg border border-page-divider" data-testid={`status-group-${grp.key || "unlinked"}`}>
              <div className="flex flex-wrap items-center gap-2 px-3 py-2">
                <StatusDot status={worst} size={10} />
                {grp.feature ? (
                  <Link to={createPageUrl(`docs?tab=features&feature=${encodeURIComponent(grp.key)}`)}
                    className="text-sm font-medium text-page-text no-underline hover:text-nav-active hover:underline inline-flex items-center gap-1">
                    <ListChecks size={13} /> {grp.feature.name}
                  </Link>
                ) : (
                  <span className="text-sm font-medium text-page-text">{grp.key ? `Requirement ${grp.key}` : "Not linked to a requirement"}</span>
                )}
                {grp.feature?.kind && grp.feature.kind !== "functional" ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-card-border/40 text-page-muted">{grp.feature.kind}</span> : null}
                <span className={`ml-auto text-xs ${testStatusMeta(worst).text}`}>
                  {c.passed}/{c.total} passed{c.failed ? ` · ${c.failed} failed` : ""}{c.error ? ` · ${c.error} error` : ""}{c.skipped ? ` · ${c.skipped} skipped` : ""}{c.none ? ` · ${c.none} never run` : ""}
                </span>
              </div>
              <ul className="px-3 pb-2 space-y-1">
                {grp.tests.map((t) => {
                  const l = t.latest; const m = testStatusMeta(l?.status);
                  return (
                    <li key={t.key} className="text-xs rounded-md px-2 py-1.5" style={{ background: "var(--table-stripe)" }}
                      data-testid={`status-test-${t.key}`} data-status={l?.status || "none"}>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <StatusDot status={l?.status} />
                        <span className="text-page-text">{t.name}</span>
                        <span className="text-[10px] px-1 py-0.5 rounded bg-card-border/40 text-page-muted font-mono">{t.kind}</span>
                        <span className={`ml-auto text-[11px] ${m.text}`}>
                          {l ? `${m.label}${l.duration_ms != null ? ` · ${l.duration_ms} ms` : ""} · ${l.env || data?.env || ""} · ${when(l.run_at)}` : "never run"}
                        </span>
                      </div>
                      {l?.message ? <div className={`text-[11px] mt-0.5 ${l.status === "passed" ? "text-page-muted" : m.text}`}>{l.message}</div> : null}
                      <ResultDetails latest={l} />
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>

      {data?.runs?.length ? (
        <div className="space-y-1" data-testid="status-runs">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-page-muted pt-2">Run history</div>
          {data.runs.map((r) => (
            <div key={r.id} className="rounded-md border border-page-divider">
              <button type="button" onClick={() => showRun(r.id)} data-testid={`status-run-${r.id}`}
                className="w-full text-left flex flex-wrap items-center gap-2 px-3 py-1.5 text-xs bg-transparent border-0 cursor-pointer text-page-text hover:bg-card-border/20">
                {openRun === r.id ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                <span className="font-mono">#{r.id}</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] ${RUN_TONE[r.status] || RUN_TONE.error}`}>{r.status}</span>
                <span className="text-page-muted">{r.env} · {r.trigger}{r.scope?.feature_key ? ` · requirement ${r.scope.feature_key}` : r.scope?.keys?.length ? ` · ${r.scope.keys.length} test(s)` : " · all"}</span>
                <span className="ml-auto text-page-muted">
                  {r.status === "running" ? "running…" : `${r.passed}/${r.total} passed${r.failed ? `, ${r.failed} failed` : ""}${r.errors ? `, ${r.errors} error` : ""}${r.skipped ? `, ${r.skipped} skipped` : ""}`}
                  {" · "}{when(r.finished_at || r.started_at)}
                </span>
              </button>
              {openRun === r.id ? (
                <div className="px-3 pb-2 text-xs">
                  {r.error ? <div className="text-red-600 mb-1">{r.error}</div> : null}
                  {!runDetail ? <Loader2 size={12} className="animate-spin text-page-muted" /> : runDetail.error ? <div className="text-red-600">{runDetail.error}</div> : (
                    <ul className="space-y-0.5">
                      {(runDetail.results || []).map((x) => (
                        <li key={x.id || x.key} className="flex flex-wrap items-center gap-1.5">
                          <StatusDot status={x.status} /> <span>{x.name || x.key}</span>
                          <span className={`ml-auto ${testStatusMeta(x.status).text}`}>{x.status}{x.duration_ms != null ? ` · ${x.duration_ms} ms` : ""}</span>
                          {x.message && x.status !== "passed" ? <div className="basis-full text-[11px] text-page-muted pl-4">{x.message}</div> : null}
                        </li>
                      ))}
                      {!(runDetail.results || []).length ? <li className="text-page-muted">no results in this run</li> : null}
                    </ul>
                  )}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
